import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import Styles from './styles.module.scss';
import {useDispatch, useSelector} from "react-redux";
import Moment from "react-moment";
import moment from "moment";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {getTransactions} from "../../store/cabinet/action";
import {formatNumber} from "../cabinetShell/config";

import heroArt from '../../theme/assets/redesign/transactions/hero.webp';
import heroArtMobile from '../../theme/assets/redesign/transactions/hero_mobile.webp';
import catRefill from '../../theme/assets/redesign/transactions/cat_refill.svg';
import catPurchase from '../../theme/assets/redesign/transactions/cat_purchase.svg';
import catGiveaway from '../../theme/assets/redesign/transactions/cat_giveaway.svg';
import catReferral from '../../theme/assets/redesign/transactions/cat_referral.svg';
import catEvent from '../../theme/assets/redesign/transactions/cat_event.svg';
import copyIcon from '../../theme/assets/redesign/transactions/copy.svg';
import sortIcon from '../../theme/assets/redesign/transactions/sort.svg';
import calendarIcon from '../../theme/assets/redesign/transactions/calendar.svg';
import chevronDown from '../../theme/assets/redesign/transactions/chevron_down.svg';
import chevronRight from '../../theme/assets/redesign/transactions/chevron_right.svg';


// Фильтры = категории бэка (?category=...)
const FILTERS = [
    {key: false, label: 'Все'},
    {key: 'matrix', label: 'Покупка'},
    {key: 'referral', label: 'Реферальные'},
    {key: 'contest', label: 'Розыгрыши'},
    {key: 'events', label: 'События'},
];

// категория из API -> иконка и подпись из макета
const CATEGORY = {
    matrix: {icon: catPurchase, label: 'Покупка'},
    referral: {icon: catReferral, label: 'Реферальные'},
    contest: {icon: catGiveaway, label: 'Розыгрыш'},
    events: {icon: catEvent, label: 'Событие'},
    event: {icon: catEvent, label: 'Событие'},
};
const CATEGORY_FALLBACK = {icon: catRefill, label: 'Пополнение'};

const TYPE = {
    receive: 'Пополнение',
    withdraw: 'Вывод',
};

const STATUS = {
    confirmed: {label: 'Подтвержден', tone: 'positive'},
    created: {label: 'В обработке', tone: 'warning'},
    pending: {label: 'В обработке', tone: 'warning'},
    canceled: {label: 'Отменен', tone: 'muted'},
    expired: {label: 'Время истекло', tone: 'danger'},
};

const getHash = (item) => item?.hash || item?.tx_hash || item?.txid || item?.transaction_hash || '';
const shortHash = (h) => (h && h.length > 12 ? `${h.slice(0, 5)}…${h.slice(-4)}` : h || '—');

const SORTERS = {
    date: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    category: (a, b) => String(a.category || '').localeCompare(String(b.category || '')),
    amount: (a, b) => (Number(a.amount) || 0) - (Number(b.amount) || 0),
    type: (a, b) => String(a.type || '').localeCompare(String(b.type || '')),
    status: (a, b) => String(a.status || '').localeCompare(String(b.status || '')),
    hash: (a, b) => getHash(a).localeCompare(getHash(b)),
};


const CabinetTransactionsMain = () => {
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const transactions = useSelector(s => s.cabinet.transactions);
    const loading = useSelector(s => Boolean(s.cabinet.transactionsLoading ?? s.cabinet.transactionsStarted));
    const error = useSelector(s => s.cabinet.transactionsErr);

    const [category, setCategory] = useState(false);
    const [period, setPeriod] = useState({from: '', to: ''});
    const [periodOpen, setPeriodOpen] = useState(false);
    const [sort, setSort] = useState({key: 'date', dir: 'desc'});
    const [expanded, setExpanded] = useState(null);

    const currentPage = transactions?.transactions?.current_page || 1;
    const lastPage = transactions?.transactions?.last_page || 1;

    const boxRef = useRef(null);
    const inFlightRef = useRef(false);
    const lastRequestedPageRef = useRef(null);

    // первая загрузка и при смене фильтра / периода
    useEffect(() => {
        inFlightRef.current = false;
        lastRequestedPageRef.current = null;
        dispatch(getTransactions(category, 1, period));
        boxRef.current?.scrollTo({top: 0, behavior: 'auto'});
    }, [dispatch, category, period]);

    // догрузка при прокрутке (на десктопе скроллится таблица, на мобилке — страница)
    const loadMore = useCallback(() => {
        if (inFlightRef.current || loading || currentPage >= lastPage) return;
        const nextPage = currentPage + 1;
        inFlightRef.current = true;
        lastRequestedPageRef.current = nextPage;
        dispatch(getTransactions(category, nextPage, period));
    }, [loading, currentPage, lastPage, category, period, dispatch]);

    useEffect(() => {
        const el = boxRef.current;
        const onBoxScroll = () => {
            if (el && el.scrollHeight > el.clientHeight && el.scrollTop + el.clientHeight >= el.scrollHeight - 100) loadMore();
        };
        const onWindowScroll = () => {
            if (window.innerHeight + window.scrollY >= document.body.scrollHeight - 200) loadMore();
        };
        el?.addEventListener('scroll', onBoxScroll, {passive: true});
        window.addEventListener('scroll', onWindowScroll, {passive: true});
        return () => {
            el?.removeEventListener('scroll', onBoxScroll);
            window.removeEventListener('scroll', onWindowScroll);
        };
    }, [loadMore]);

    useEffect(() => {
        const loadedPage = transactions?.transactions?.current_page;
        if (inFlightRef.current && loadedPage === lastRequestedPageRef.current) inFlightRef.current = false;
    }, [transactions]);

    useEffect(() => {
        if (error) inFlightRef.current = false;
    }, [error]);

    // сортировка по колонкам — по уже загруженным строкам
    const rows = useMemo(() => {
        const list = Array.isArray(transactions?.transactions?.data) ? [...transactions.transactions.data] : [];
        const sorter = SORTERS[sort.key];
        if (sorter) list.sort((a, b) => (sort.dir === 'asc' ? sorter(a, b) : sorter(b, a)));
        return list;
    }, [transactions, sort]);

    const toggleSort = (key) => setSort(s => ({key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc'}));

    const copyHash = (hash) => {
        if (!hash) return;
        navigator.clipboard.writeText(hash);
        toast(t('Скопировано'));
    };

    const periodLabel = period.from || period.to
        ? `${period.from ? moment(period.from).format('DD.MM.YYYY') : '…'} – ${period.to ? moment(period.to).format('DD.MM.YYYY') : '…'}`
        : t('За всё время');

    const SortHead = ({id, label, className}) => (
        <button type="button" className={`${Styles.sortHead} ${className || ''} ${sort.key === id ? Styles.sortHeadActive : ''}`} onClick={() => toggleSort(id)}>
            <span>{label}</span>
            <img src={sortIcon} alt="" width="12" height="12"/>
        </button>
    );

    const periodPicker = (
        <div className={Styles.period}>
            <button type="button" className={Styles.periodBtn} onClick={() => setPeriodOpen(o => !o)}>
                <img src={calendarIcon} alt="" width="18" height="18"/>
                <span>{periodLabel}</span>
                <img className={`${Styles.periodChevron} ${periodOpen ? Styles.periodChevronOpen : ''}`} src={chevronDown} alt="" width="14" height="14"/>
            </button>
            {periodOpen ? (
                <PeriodDropdown
                    t={t}
                    value={period}
                    onApply={(v) => { setPeriod(v); setPeriodOpen(false); }}
                    onClose={() => setPeriodOpen(false)}
                />
            ) : null}
        </div>
    );

    const filters = (
        <div className={Styles.filters}>
            {FILTERS.map(f => (
                <button
                    type="button"
                    key={String(f.key)}
                    className={`${Styles.chip} ${category === f.key ? Styles.chipActive : ''}`}
                    onClick={() => setCategory(f.key)}
                >
                    {t(f.label)}
                </button>
            ))}
        </div>
    );

    return (
        <section className={Styles.container}>
            {/* ===== Hero ===== */}
            <div className={Styles.hero}>
                <div className={Styles.heroArt}>
                    <img className={Styles.desktopOnly} src={heroArt} alt=""/>
                    <img className={Styles.mobileOnly} src={heroArtMobile} alt=""/>
                </div>
                <div className={Styles.heading}>
                    <div className={Styles.title}>{t('Транзакции')}</div>
                    <p className={Styles.subtitle}>{t('Отслеживайте все свои транзакции: покупки токенов, пополнения, награды и достижения')}.</p>
                </div>
                <div className={`${Styles.controls} ${Styles.desktopOnly}`}>
                    {filters}
                    {periodPicker}
                </div>
            </div>

            <div className={`${Styles.mobileControls} ${Styles.mobileOnly}`}>
                {filters}
                {periodPicker}
            </div>

            {/* ===== Таблица ===== */}
            <div className={Styles.table}>
                <div className={`${Styles.header} ${Styles.desktopOnly}`}>
                    <SortHead id="date" label={t('Дата')} className={Styles.colDate}/>
                    <SortHead id="category" label={t('Категория')} className={Styles.colCategory}/>
                    <SortHead id="amount" label={t('Сумма')} className={Styles.colAmount}/>
                    <SortHead id="type" label={t('Тип')} className={Styles.colType}/>
                    <SortHead id="status" label={t('Статус')} className={Styles.colStatus}/>
                    <SortHead id="hash" label="TX Hash" className={Styles.colHash}/>
                </div>
                <div className={`${Styles.header} ${Styles.mobileOnly}`}>
                    <SortHead id="date" label={t('Дата')} className={Styles.mColDateHead}/>
                    <div className={Styles.mColAmount}>{t('Сумма')}</div>
                    <div className={Styles.mColType}>{t('Тип')}</div>
                    <div className={Styles.mColStatus}>{t('Статус')}</div>
                    <div className={Styles.mColChevron}/>
                </div>

                <div className={Styles.body} ref={boxRef}>
                    {rows.length > 0 ? rows.map((item, index) => {
                        const id = item.id ?? index;
                        const cat = CATEGORY[item.category] || CATEGORY_FALLBACK;
                        const status = STATUS[item.status] || {label: item.status, tone: 'muted'};
                        const hash = getHash(item);
                        const isOpen = expanded === id;
                        const amount = `${formatNumber(item.amount, 6)} ${item?.currency?.code || ''}`;
                        const typeLabel = t(TYPE[item.type] || item.type || '—');
                        const badge = <span className={`${Styles.badge} ${Styles[`badge_${status.tone}`]}`}>{t(status.label)}</span>;
                        return (
                            <div className={`${Styles.row} ${isOpen ? Styles.rowOpen : ''}`} key={`tx-${id}`}>
                                {/* десктоп */}
                                <div className={`${Styles.rowMain} ${Styles.desktopOnly}`}>
                                    <div className={`${Styles.colDate} ${Styles.date}`}>
                                        <Moment format="DD.MM.YYYY">{item.created_at}</Moment>
                                        <Moment format="HH:mm">{item.created_at}</Moment>
                                    </div>
                                    <div className={`${Styles.colCategory} ${Styles.category}`}>
                                        <img src={cat.icon} alt="" width="22" height="22"/>
                                        <span>{t(cat.label)}</span>
                                    </div>
                                    <div className={`${Styles.colAmount} ${Styles.amount}`}>{amount}</div>
                                    <div className={`${Styles.colType} ${Styles.text}`}>{typeLabel}</div>
                                    <div className={Styles.colStatus}>{badge}</div>
                                    <div className={`${Styles.colHash} ${Styles.hash}`}>
                                        <span>{shortHash(hash)}</span>
                                        {hash ? (
                                            <button type="button" onClick={() => copyHash(hash)} aria-label="copy">
                                                <img src={copyIcon} alt="" width="16" height="16"/>
                                            </button>
                                        ) : null}
                                    </div>
                                </div>

                                {/* мобилка: по тапу раскрываются категория и хэш */}
                                <div className={`${Styles.rowMain} ${Styles.mobileOnly}`} onClick={() => setExpanded(isOpen ? null : id)}>
                                    <div className={`${Styles.mColDate} ${Styles.date}`}>
                                        <Moment format="DD.MM.YYYY">{item.created_at}</Moment>
                                        <Moment format="HH:mm">{item.created_at}</Moment>
                                    </div>
                                    <img className={Styles.mCatIcon} src={cat.icon} alt=""/>
                                    <div className={`${Styles.mColAmount} ${Styles.amount}`}>{amount}</div>
                                    <div className={`${Styles.mColType} ${Styles.text}`}>{typeLabel}</div>
                                    <div className={Styles.mColStatus}>{badge}</div>
                                    <img className={`${Styles.mColChevron} ${isOpen ? Styles.chevronOpen : ''}`} src={chevronRight} alt=""/>
                                </div>
                                {isOpen ? (
                                    <div className={`${Styles.details} ${Styles.mobileOnly}`}>
                                        <div className={Styles.detailsRow}>
                                            <span>{t('Категория')}</span>
                                            <span>{t(cat.label)}</span>
                                        </div>
                                        <div className={Styles.detailsRow}>
                                            <span>TX Hash</span>
                                            <span className={Styles.hash}>
                                                <span>{shortHash(hash)}</span>
                                                {hash ? (
                                                    <button type="button" onClick={() => copyHash(hash)} aria-label="copy">
                                                        <img src={copyIcon} alt="" width="16" height="16"/>
                                                    </button>
                                                ) : null}
                                            </span>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        );
                    }) : (!loading ? <div className={Styles.empty}>{t('Транзакций пока нет')}</div> : null)}

                    {loading ? <div className={Styles.loader}>{t('Загрузка…')}</div> : null}
                </div>
            </div>
        </section>
    );
};


// Выпадашка выбора периода: две даты + быстрые варианты
const PeriodDropdown = ({t, value, onApply, onClose}) => {
    const [from, setFrom] = useState(value.from);
    const [to, setTo] = useState(value.to);
    const preset = (days) => {
        setFrom(moment().subtract(days, 'days').format('YYYY-MM-DD'));
        setTo(moment().format('YYYY-MM-DD'));
    };
    return (
        <>
            <div className={Styles.periodOverlay} onClick={onClose}/>
            <div className={Styles.periodDropdown}>
                <div className={Styles.periodPresets}>
                    <button type="button" onClick={() => preset(7)}>{t('7 дней')}</button>
                    <button type="button" onClick={() => preset(30)}>{t('30 дней')}</button>
                    <button type="button" onClick={() => preset(90)}>{t('90 дней')}</button>
                </div>
                <label className={Styles.periodField}>
                    <span>{t('С')}</span>
                    <input type="date" value={from} max={to || undefined} onChange={e => setFrom(e.target.value)}/>
                </label>
                <label className={Styles.periodField}>
                    <span>{t('По')}</span>
                    <input type="date" value={to} min={from || undefined} onChange={e => setTo(e.target.value)}/>
                </label>
                <div className={Styles.periodActions}>
                    <button type="button" className={Styles.periodReset} onClick={() => onApply({from: '', to: ''})}>{t('Сбросить')}</button>
                    <button type="button" className={Styles.periodApply} onClick={() => onApply({from, to})}>{t('Применить')}</button>
                </div>
            </div>
        </>
    );
};

export default CabinetTransactionsMain;
